/**
 * Cookie Utility Functions
 * 쿠키 값을 가져오고 저장하는 유틸리티 함수
 */

interface CookieOptions {
    expires?: number | Date; // 만료일 (일 수 또는 Date 객체)
    path?: string; // 경로
    domain?: string; // 도메인
    secure?: boolean; // HTTPS만
    sameSite?: 'strict' | 'lax' | 'none'; // SameSite 속성
}

/**
 * 쿠키에서 값을 가져옵니다.
 * @param name 쿠키 이름
 * @returns 쿠키 값 또는 null
 */
export const getCookie = (name: string): string | null => {
    try {
        const nameEQ = name + '=';
        const cookies = document.cookie.split(';');
        
        for (let i = 0; i < cookies.length; i++) {
            let cookie = cookies[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1, cookie.length);
            }
            if (cookie.indexOf(nameEQ) === 0) {
                return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
            }
        }
        return null;
    } catch (error) {
        return null;
    }
};

/**
 * 쿠키에 값을 저장합니다.
 * @param name 쿠키 이름
 * @param value 쿠키 값
 * @param options 쿠키 옵션
 */
export const setCookie = (name: string, value: string, options?: CookieOptions): void => {
    let cookieString = `${name}=${encodeURIComponent(value)}`;
    
    if (options) {
        if (options.expires) {
            if (typeof options.expires === 'number') {
                const date = new Date();
                date.setTime(date.getTime() + options.expires * 24 * 60 * 60 * 1000);
                cookieString += `; expires=${date.toUTCString()}`;
            } else {
                cookieString += `; expires=${options.expires.toUTCString()}`;
            }
        }
        
        if (options.path) {
            cookieString += `; path=${options.path}`;
        }
        
        if (options.domain) {
            cookieString += `; domain=${options.domain}`;
        }
        
        if (options.secure) {
            cookieString += `; secure`;
        }
        
        if (options.sameSite) {
            cookieString += `; samesite=${options.sameSite}`;
        }
    }
    
    document.cookie = cookieString;
};

/**
 * 쿠키를 삭제합니다.
 * @param name 쿠키 이름
 * @param path 경로 (설정했던 경로와 동일해야 함)
 * @param domain 도메인 (설정했던 도메인과 동일해야 함)
 */
export const removeCookie = (name: string, path?: string, domain?: string): void => {
    let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
    
    if (path) {
        cookieString += `; path=${path}`;
    }
    
    if (domain) {
        cookieString += `; domain=${domain}`;
    }
    
    document.cookie = cookieString;
};

/**
 * 모든 쿠키를 가져옵니다.
 * @returns 쿠키 객체
 */
export const getAllCookies = (): Record<string, string> => {
    try {
        const cookies: Record<string, string> = {};
        const cookieArray = document.cookie.split(';');
        
        for (let i = 0; i < cookieArray.length; i++) {
            const cookie = cookieArray[i].trim();
            if (cookie) {
                const [name, ...valueParts] = cookie.split('=');
                cookies[name] = decodeURIComponent(valueParts.join('='));
            }
        }
        
        return cookies;
    } catch (error) {
        return {};
    }
};

